// Panneau de session : qui je suis, sur quel appareil, avec quelles clés.
//
// Les empreintes affichées ne sont pas décoratives : elles permettent de
// vérifier d'un coup d'œil que la clé maîtresse et l'identité MLS de l'appareil
// sont bien les mêmes d'une session à l'autre.

import { useState } from 'react';
import { CabinetAudit } from '../audit/AuditPanel';
import { ChangePassword } from './ChangePassword';
import { DeviceApprovals } from './Devices';
import { InviteToCabinet } from './InviteToCabinet';
import { HeldShares, Recovery } from './Recovery';
import { RecoveryRequests } from './RecoveryRequests';
import { TwoFactor } from './TwoFactor';
import { logout } from './opaque';
import { useAuth } from './store';

/** Empreinte lisible : les 8 premiers octets suffisent à comparer à l'œil. */
function shortFingerprint(fingerprint: Uint8Array): string {
  return Array.from(fingerprint.slice(0, 8))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
}

export function SignedIn() {
  const session = useAuth((s) => s.session);
  const me = useAuth((s) => s.me);
  const masterKey = useAuth((s) => s.masterKey);
  const device = useAuth((s) => s.device);
  const signOut = useAuth((s) => s.signOut);
  const [busy, setBusy] = useState(false);

  if (!session || !me || !masterKey) return null;

  async function onLogout() {
    setBusy(true);
    try {
      await logout(session!.sessionToken);
    } finally {
      // Même si le serveur refuse, on lâche la session locale (et l'exportKey).
      signOut();
    }
  }

  return (
    <section className="mx-auto mt-10 max-w-sm text-left" data-testid="signed-in">
      <p className="text-sm font-medium text-emerald-700">Session ouverte</p>

      <dl className="mt-4 space-y-1 text-sm">
        <Row label="Utilisateur" value={me.userId} />
        <Row label="Cabinet" value={me.tenantId} />
        {/* `me.deviceId` est lu avant l'enrôlement de l'appareil : c'est le
            store qui porte l'information à jour. */}
        <Row
          label="Appareil"
          value={device?.deviceId ?? me.deviceId ?? '— (non enrôlé)'}
        />
        <Row label="Session valide jusqu'à" value={session.expiresAt} />
        <Row
          label="Empreinte de la clé maîtresse"
          value={shortFingerprint(masterKey.fingerprint)}
          testId="master-key-fingerprint"
        />
        {device && (
          <Row
            label="Identité MLS de l'appareil"
            value={shortFingerprint(device.client.signaturePublicKey())}
            testId="device-identity"
          />
        )}
      </dl>

      <p className="mt-4 text-xs text-slate-400">
        {masterKey.created
          ? 'Clé maîtresse créée (256 bits aléatoires) et déposée enveloppée : le serveur ne peut pas l’ouvrir.'
          : 'Clé maîtresse déverrouillée depuis son enveloppe. Elle est stable à vie — changer de mot de passe ne fait que la ré-envelopper.'}
      </p>

      <p
        data-testid="device-status"
        className={`mt-2 text-xs ${device ? 'text-slate-400' : 'text-amber-700'}`}
      >
        {!device
          ? 'Appareil non enrôlé : approuve-le depuis ton appareil principal pour accéder aux dossiers.'
          : device.created
            ? 'Appareil enrôlé : identité MLS créée, puis déposée chiffrée sur le serveur.'
            : 'Identité MLS restaurée depuis son état chiffré — c’est le même appareil, il garde sa place dans ses groupes.'}
      </p>

      <DeviceApprovals />

      {/* En tête : une récupération en cours prime sur tout le reste — un
          confrère est enfermé dehors, et je suis l'une des deux clés. */}
      <RecoveryRequests />

      <HeldShares />

      {/* Le second facteur avant le reste : c'est le seul rempart devant le mot
          de passe, qui ouvre la clé maîtresse — donc tout. */}
      <TwoFactor />

      <Recovery />

      <CabinetAudit />

      <InviteToCabinet />

      <ChangePassword />

      <button
        type="button"
        onClick={onLogout}
        disabled={busy}
        data-testid="logout"
        className="mt-3 w-full rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      >
        {busy ? 'Déconnexion…' : 'Se déconnecter'}
      </button>
    </section>
  );
}

function Row({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) {
  return (
    <div className="flex gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd
        data-testid={testId}
        className="ml-auto truncate font-mono text-xs text-slate-700"
      >
        {value}
      </dd>
    </div>
  );
}
